import React, { useState, useEffect } from 'react';
import { Edit3, RotateCcw, Trash2 } from 'lucide-react';

const ROW_GRID = 'grid grid-cols-[1fr_repeat(11,48px)_36px] items-center';

export default function MaterialRow({ plan, component, showSeatPlaced, mdpInfo, mappingCodes, ksuToTc, onUpdate, onDelete, onReschedule, onQuickEdit, isAdmin = true, onCreatePlan }) {
  const p = plan;
  const [editing, setEditing] = useState(false);
  const [qty, setQty] = useState(p ? p.planned_quantity : 0);
  const spu = p ? (p.seats_per_unit || 0) : (component?.seats_per_unit || 0);
  const desc = p ? p.material_description : (component?.material_description || '');
  const plannedQty = p ? p.planned_quantity : 0;
  const completedQty = p ? (p.completed_quantity || 0) : 0;

  const mdp = mdpInfo?.period_mdp ?? 0;
  const totPlan = mdpInfo?.total_planned ?? 0;
  const cumProd = mdpInfo?.total_completed ?? 0;
  const cumShip = mdpInfo?.total_shipped ?? 0;
  const shProd = mdpInfo?.shortage_produced ?? 0;
  const shShip = mdpInfo?.shortage_shipped ?? 0;
  const remainingPlans = Math.max(0, totPlan - cumProd);
  const shPlan = Math.max(0, shProd - remainingPlans);
  const remaining = mdpInfo?.remaining ?? 0;
  const totalMdp = mdpInfo?.total_mdp ?? 0;
  const carryover = mdpInfo?.carryover ?? 0;

  const kielCode = mdpInfo?.kiel_material_code || mappingCodes?.ksu || component?.material_code || '';
  const custCode = mdpInfo?.customer_material_code || mappingCodes?.tc || (ksuToTc && kielCode ? ksuToTc[kielCode] : '') || '';

  useEffect(() => { setQty(plannedQty); }, [plannedQty]);

  const handleSave = () => {
    const newQty = parseInt(qty) || 0;
    if (p && newQty !== p.planned_quantity) onQuickEdit(p.id, { planned_quantity: newQty });
    setEditing(false);
  };

  const handlePlnClick = () => {
    if (!isAdmin) return;
    if (p) {
      setEditing(true);
    } else if (onCreatePlan && component) {
      // No plan exists yet for this component - create one
      onCreatePlan(component);
    }
  };

  return (
    <div className={`${ROW_GRID} px-4 py-2 hover:bg-blue-50/30 group border-b border-slate-50 last:border-b-0`} data-testid={`material-row-${p?.id || desc}`}>
      {/* Component info */}
      <div className="pr-2 overflow-hidden">
        <div className="text-[13px] font-bold text-slate-800 leading-tight truncate" title={desc}>{desc}</div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          {custCode && <span className="text-[10px] text-slate-400 font-mono">{custCode}</span>}
          {kielCode && <span className="text-[10px] text-slate-300 font-mono">{kielCode}</span>}
          {showSeatPlaced && spu > 0 && (
            <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1 py-px rounded">{spu === 2 ? '2P' : '1P'}</span>
          )}
          {carryover !== 0 && (
            <span className={`text-[9px] font-bold px-1 py-px rounded ${carryover > 0 ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50'}`}>
              CO:{carryover > 0 ? '+' : ''}{carryover}
            </span>
          )}
          {p?.set_numbers && (
            <span className="text-[9px] font-bold text-indigo-700 bg-indigo-50 px-1.5 py-px rounded border border-indigo-200" title={p.set_numbers}>
              {p.set_numbers}
            </span>
          )}
        </div>
      </div>

      {/* 10 data columns */}
      <Cell v={mdp} c="text-amber-600" />
      <Cell v={totPlan} c="text-slate-600" />
      {isAdmin && editing ? (
        <input type="number" min="0" value={qty} onChange={e => setQty(e.target.value)}
          onBlur={handleSave} onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
          className="w-full border border-blue-400 rounded text-center text-xs font-bold text-blue-600 focus:outline-none"
          autoFocus data-testid={`edit-qty-${p?.id}`} />
      ) : (
        <span className={`text-sm font-bold text-center border-r border-slate-200 ${isAdmin ? 'text-blue-600 cursor-pointer hover:bg-blue-100 rounded' : p ? 'text-blue-600' : 'text-slate-300'}`}
          onClick={handlePlnClick} data-testid={`plan-qty-${p?.id || desc}`}>{plannedQty}</span>
      )}
      <Cell v={completedQty} c={completedQty > 0 ? 'text-slate-700' : 'text-slate-300'} />
      <Cell v={cumProd} c={cumProd > 0 ? 'text-green-600' : 'text-slate-300'} />
      <Cell v={cumShip} c={cumShip > 0 ? 'text-blue-600' : 'text-slate-300'} />
      <Cell v={shProd} c={shProd > 0 ? 'text-red-500' : 'text-slate-300'} />
      <Cell v={shShip} c={shShip > 0 ? 'text-orange-500' : 'text-slate-300'} />
      <Cell v={shPlan} c={shPlan > 0 ? 'text-purple-600' : 'text-slate-300'} />
      <Cell v={remaining} c={remaining > 0 ? 'text-orange-600 font-extrabold' : 'text-slate-300'} />
      <Cell v={totalMdp} c="text-slate-700" last />

      {/* Action buttons */}
      <div className={`flex gap-px justify-center ${p && isAdmin ? 'opacity-0 group-hover:opacity-100' : 'pointer-events-none opacity-0'} transition-opacity`}>
        {p && isAdmin && (
          <>
            <button onClick={() => onUpdate(p)} className="p-1 hover:bg-blue-100 rounded" data-testid={`edit-${p.id}`}><Edit3 className="w-3 h-3 text-blue-500" /></button>
            <button onClick={() => onReschedule(p)} className="p-1 hover:bg-purple-100 rounded" data-testid={`reschedule-${p.id}`}><RotateCcw className="w-3 h-3 text-purple-500" /></button>
            <button onClick={() => onDelete(p.id)} className="p-1 hover:bg-red-100 rounded" data-testid={`delete-${p.id}`}><Trash2 className="w-3 h-3 text-red-400" /></button>
          </>
        )}
      </div>
    </div>
  );
}

function Cell({ v, c, last }) {
  return <span className={`text-sm font-bold text-center ${c} ${last ? '' : 'border-r border-slate-200'}`}>{v}</span>;
}

export { ROW_GRID };
